import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { AccountModelPrivatization } from "./AccountModelPrivatization";
import { ActiveUser } from "../core/ActiveUser";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { AccountLookupService } from "../../lookup/model/impl/AccountLookupService";
import { BranchLookupService } from "../../lookup/model/impl/BranchLookupService";
import { ClientLookupService } from "../../lookup/model/impl/ClientLookupService";
import { UserLookupService } from "../../lookup/model/impl/UserLookupService";
import { ClientModelPrivatization } from "./ClientModelPrivatization";
import { AccountPromiseLookupService } from "../../lookup/model/impl/AccountPromiseLookupService";
import { BranchPromiseLookupService } from "../../lookup/model/impl/BranchPromiseLookupService";
import { ClientPromiseLookupService } from "../../lookup/model/impl/ClientPromiseLookupService";
import { UserPromiseLookupService } from "../../lookup/model/impl/UserPromiseLookupService";
import { ClientTraderModelPrivatization } from "./ClientTraderModelPrivatization";
import { SystemConfigPromiseService } from "shared/auth-services";
import { SysFeatures } from "../core/SysFeatures";

import { BasePrivatizationService } from "./BasePrivatizationService";
import { StaticDataPrivatizationType } from "./enum/StaticDataPrivatizationType";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { CubiqWorker } from "../../worker/CubiqWorker";

@Injectable()
export class OrderDataPrivatizationService extends BasePrivatizationService {
  private static _instance;
  private prefix = "__bfx_privatization";
  private activeUser: ActiveUser;

  private clientSegmentRole: boolean = false;
  private clientDataRole: boolean = false;
  private privateLimitOrders: boolean = false;
  private privateBenchmarkOrders: boolean = false;
  // no british english in code.
  private privatizeClientComments: boolean;
  private privatizePrivateComments: boolean;

  private readyResolve;
  private readyReject;

  public readonly ready = new Promise<void>((res, rej) => {
    this.readyResolve = res;
    this.readyReject = rej;
  });

  public constructor(
    cubiqWorker: CubiqWorker,
    accountPromiseLookup: AccountPromiseLookupService,
    branchPromiseLookup: BranchPromiseLookupService,
    clientPromiseLookup: ClientPromiseLookupService,
    userPromiseLookup: UserPromiseLookupService,
    private userProfileService: ActiveUserProfilePromiseService,
    private sysConfigService: SystemConfigPromiseService,
    cmp: ClientModelPrivatization,
    amp: AccountModelPrivatization,
    ctmp: ClientTraderModelPrivatization,
  ) {
    super(cmp, amp, ctmp);
    cubiqWorker.isAuth(`${this.constructor.name}`).then(() => {
      Promise.all([
        accountPromiseLookup.isPrimed(),
        branchPromiseLookup.isPrimed(),
        clientPromiseLookup.isPrimed(),
        userPromiseLookup.isPrimed(),
        this.userProfileService.isPrimed(),
        this.sysConfigService.isPrimed(),
        this.cmp.ready,
        this.amp.ready,
        this.ctmp.ready,
      ]).then(
        () => {
          this.accountLookup = accountPromiseLookup.getBase();
          this.branchLookup = branchPromiseLookup.getBase();
          this.clientLookup = clientPromiseLookup.getBase();
          this.userLookup = userPromiseLookup.getBase();

          this.clientSegmentRole = this.sysConfigService.hardHasRole(
            "ROLE_VISIBILITY_CLIENT_SEGMENT",
          );
          this.clientDataRole = this.sysConfigService.hardHasRole("ROLE_VISIBILITY_CLIENT_DATA");
          this.privateLimitOrders = this.sysConfigService.hardHasRole(
            "ROLE_VISIBILITY_PRIVATE_LIMIT_ORDERS",
          );
          this.privateBenchmarkOrders = this.sysConfigService.hardHasRole(
            "ROLE_VISIBILITY_PRIVATE_BENCHMARK_ORDERS",
          );
          this.privatizePrivateComments =
            this.sysConfigService.hardGetSystemConfig(SysFeatures.privatisePrivateComments) == `Y`;
          this.privatizeClientComments =
            this.sysConfigService.hardGetSystemConfig(SysFeatures.privatiseClientComments) == `Y`;

          this.userProfileService.getActiveUserData().then(
            (activeUser) => {
              this.activeUser = activeUser;

              this.readyResolve();
            },
            () => {
              this.readyReject();
            },
          );
        },
        () => {
          this.readyReject();
        },
      );
    });
  }

  /**
   * Create a list of all allocations in the order
   * @param orderData
   * @returns
   */
  private getFlattenedAllocations(orderData: CubiqTS.OrderDataImpl): CubiqTS.AllocationDataImpl[] {
    const retAllocations = [];
    if (orderData.allocationData) {
      orderData.allocationData.forEach((allocation) => {
        retAllocations.push(allocation);
        if (allocation.componentAllocations) {
          allocation.componentAllocations.forEach((component) => retAllocations.push(component));
        }
      });
    }
    return retAllocations;
  }

  public checkPrivatization(orderData: CubiqTS.OrderDataImpl): CubiqTS.OrderDataImpl {
    if (orderData == null) {
      // logger.out.warn(`${this.constructor.name}.checkPrivatization() attempted to privatize null orderData`);
      return orderData;
    }

    const allocations = this.getFlattenedAllocations(orderData);

    const client = this.clientLookup.getClient(orderData.clientCode);

    if (this.activeUser.userCode == orderData.entryUserCode) {
      if (!client) {
        this.checkIdName(
          orderData.clientCode,
          orderData.clientName,
          orderData.accountCode,
          orderData.accountName,
        );
        StaticDataPrivatizationType.setPrivatization(
          orderData,
          StaticDataPrivatizationType.RestrictedIdName,
        );
        orderData.clientCode = this.cmp.getRestrictedIdNameKey(
          orderData.clientCode,
          orderData.clientName,
        );

        orderData.accountCode = this.amp.getRestrictedIdNameKey(
          orderData.accountCode,
          orderData.accountName,
        );
        allocations.forEach((allocation) => {
          allocation.accountCode = this.amp.getRestrictedIdNameKey(
            allocation.accountCode,
            allocation.accountName,
          );
        });
      }
      return orderData;
    }
    let checkClientContactPrivatization = false;

    if (
      orderData.isPrivate &&
      ((CubiqTS.OrderType.BENCHMARK === orderData.orderType && !this.privateBenchmarkOrders) ||
        (CubiqTS.OrderType.BENCHMARK !== orderData.orderType && !this.privateLimitOrders))
    ) {
      StaticDataPrivatizationType.setPrivatization(orderData, StaticDataPrivatizationType.Private);
      orderData.clientCode = this.cmp.getPrivateKey(client);
      orderData.clientName = this.cmp.getPrivateLabel();
      const privateKey = this.amp.getPrivateKey();
      const privateLabel = this.amp.getPrivateLabel();
      orderData.accountCode = privateKey;
      orderData.accountName = privateLabel;
      allocations.forEach((allocation) => {
        allocation.accountCode = privateKey;
        allocation.accountName = privateLabel;
      });

      if (orderData.clientTraderId != null || orderData.clientTraderName != null) {
        orderData.clientTraderId = CubiqTS.Long.fromValue(this.ctmp.getPrivateKey());
        orderData.clientTraderName = this.ctmp.getPrivateLabel();
      }

      if (this.privatizeClientComments) orderData.comments1 = this.cmp.getPrivateLabel();

      if (this.privatizePrivateComments) orderData.comments2 = this.cmp.getPrivateLabel();

      orderData.counterPartyDetails = this.privatizeCounterPartyDetails(
        orderData.counterPartyDetails,
        this.cmp.getPrivateLabel(),
        this.cmp.getClientContactIdForPrivatizationKey(orderData.clientCode),
      );

      checkClientContactPrivatization = true;
    } else if (
      ((this.activeUser.isSales() &&
        !this.activeUser.clientIdsInSalesGroupSet.has(orderData.clientCode)) ||
        (this.activeUser.isTrader() && !orderData.clientIsTraderVisible)) &&
      !this.clientSegmentRole &&
      !this.clientDataRole
    ) {
      StaticDataPrivatizationType.setPrivatization(
        orderData,
        StaticDataPrivatizationType.Restricted,
      );
      orderData.clientCode = this.cmp.getRestrictedKey();
      orderData.clientName = this.cmp.getRestrictedLabel();
      const privateKey = this.amp.getRestrictedKey();
      const privateLabel = this.amp.getRestrictedLabel();
      orderData.accountCode = privateKey;
      orderData.accountName = privateLabel;
      allocations.forEach((allocation) => {
        allocation.accountCode = privateKey;
        allocation.accountName = privateLabel;
      });

      if (orderData.clientTraderId != null || orderData.clientTraderName != null) {
        orderData.clientTraderId = CubiqTS.Long.fromValue(this.ctmp.getRestrictedKey());
        orderData.clientTraderName = this.ctmp.getRestrictedLabel();
      }

      if (this.privatizeClientComments) orderData.comments1 = this.cmp.getRestrictedLabel();

      if (this.privatizePrivateComments) orderData.comments2 = this.cmp.getRestrictedLabel();

      orderData.counterPartyDetails = this.privatizeCounterPartyDetails(
        orderData.counterPartyDetails,
        this.cmp.getRestrictedLabel(),
        this.cmp.getClientContactIdForPrivatizationKey(orderData.clientCode),
      );

      checkClientContactPrivatization = true;
    } else if (
      ((this.activeUser.isSales() &&
        !this.activeUser.clientIdsInSalesGroupSet.has(orderData.clientCode)) ||
        (this.activeUser.isTrader() && !orderData.clientIsTraderVisible)) &&
      this.clientSegmentRole &&
      !this.clientDataRole
    ) {
      StaticDataPrivatizationType.setPrivatization(
        orderData,
        StaticDataPrivatizationType.RestrictedClientSegment,
      );
      this.checkClientSegment(orderData.clientSegment);
      orderData.clientCode = this.cmp.getRestrictedClientSegmentKey(orderData.clientSegment);
      orderData.clientName = orderData.clientSegment;
      const privateKey = this.amp.getRestrictedClientSegmentKey(orderData.clientSegment);
      const privateLabel = orderData.clientSegment;
      orderData.accountCode = privateKey;
      orderData.accountName = privateLabel;
      allocations.forEach((allocation) => {
        allocation.accountCode = privateKey;
        allocation.accountName = privateLabel;
      });

      if (orderData.clientTraderId != null || orderData.clientTraderName != null) {
        orderData.clientTraderId = CubiqTS.Long.fromString(
          this.ctmp.getRestrictedClientSegmentKey(orderData.clientSegment),
        );
        orderData.clientTraderName = orderData.clientSegment;
      }

      if (this.privatizeClientComments) orderData.comments1 = orderData.clientSegment;

      if (this.privatizePrivateComments) orderData.comments2 = orderData.clientSegment;

      orderData.counterPartyDetails = this.privatizeCounterPartyDetails(
        orderData.counterPartyDetails,
        orderData.clientSegment,
        this.cmp.getClientContactIdForPrivatizationKey(orderData.clientCode),
      );

      checkClientContactPrivatization = true;
    } else if (
      this.branchLookup.isPrivateBranchCode(orderData.clientBranchCode) &&
      this.activeUser.branchCd != orderData.clientBranchCode
    ) {
      StaticDataPrivatizationType.setPrivatization(
        orderData,
        StaticDataPrivatizationType.BranchPrivate,
      );
      orderData.clientCode = this.cmp.getBranchPrivateKey(orderData.clientBranchCode, client);
      orderData.clientName = this.cmp.getBranchPrivateLabel(orderData.clientBranchCode);
      const privateKey = this.amp.getBranchPrivateKey(orderData.clientBranchCode);
      const privateLabel = this.amp.getBranchPrivateLabel(orderData.clientBranchCode);
      orderData.accountCode = privateKey;
      orderData.accountName = privateLabel;
      allocations.forEach((allocation) => {
        allocation.accountCode = privateKey;
        allocation.accountName = privateLabel;
      });

      if (orderData.clientTraderId != null || orderData.clientTraderName != null) {
        orderData.clientTraderId = CubiqTS.Long.fromString(
          this.ctmp.getBranchPrivateKey(orderData.clientBranchCode),
        );
        orderData.clientTraderName = this.ctmp.getBranchPrivateLabel(orderData.clientBranchCode);
      }

      if (this.privatizeClientComments)
        orderData.comments1 = this.cmp.getBranchPrivateLabel(orderData.clientBranchCode);

      if (this.privatizePrivateComments)
        orderData.comments2 = this.cmp.getBranchPrivateLabel(orderData.clientBranchCode);

      orderData.counterPartyDetails = this.privatizeCounterPartyDetails(
        orderData.counterPartyDetails,
        this.cmp.getBranchPrivateLabel(orderData.clientBranchCode),
        this.cmp.getClientContactIdForPrivatizationKey(orderData.clientCode),
      );

      checkClientContactPrivatization = true;
    } else if (!client) {
      this.checkIdName(
        orderData.clientCode,
        orderData.clientName,
        orderData.accountCode,
        orderData.accountName,
      );
      StaticDataPrivatizationType.setPrivatization(
        orderData,
        StaticDataPrivatizationType.RestrictedIdName,
      );
      orderData.clientCode = this.cmp.getRestrictedIdNameKey(
        orderData.clientCode,
        orderData.clientName,
      );
      orderData.accountCode = this.amp.getRestrictedIdNameKey(
        orderData.accountCode,
        orderData.accountName,
      );

      allocations.forEach((allocation) => {
        allocation.accountCode = this.amp.getRestrictedIdNameKey(
          allocation.accountCode,
          allocation.accountName,
        );
      });
    }

    if (
      checkClientContactPrivatization &&
      Array.isArray(orderData.clientContactIds) &&
      orderData.clientContactIds.length > 0
    ) {
      orderData.clientContactIds = this.clientLookup
        .getClientContacts(orderData.clientCode)
        .map((clientContact) => clientContact.id);
    }

    return orderData;
  }

  public isReady(): Promise<void> {
    return this.ready;
  }
}
